import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import { SharedModule } from "../../shared/shared.module";
import { Router } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
import { AuthService, SessionUserService, IOpenIDClaims } from "core-client/api";
import { consumePostLoginUrl } from "../../guards/post-login-url";

// Error types for categorization
export enum AuthErrorType {
  NETWORK = "NETWORK",
  PROVIDER = "PROVIDER",
  CONFIG = "CONFIG",
  TOKEN = "TOKEN",
  USER = "USER",
  TIMEOUT = "TIMEOUT",
  UNKNOWN = "UNKNOWN",
}

// Error details interface
export interface AuthErrorDetails {
  title: string;
  message: string;
}

@Component({
  selector: "app-auth-callback",
  imports: [SharedModule],
  templateUrl: "./auth-callback.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./auth-callback.component.scss"],
})
export class AuthCallbackComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject<void>();
  private readonly SCHEDULED_REDIRECT_TIMEOUT: number = 5000;
  private loginRedirectTimeout?: ReturnType<typeof setTimeout>;

  // UI state
  hasError: boolean = false;
  loadingMessage: string = "PAGE_IS_LOADING";

  // Error display properties
  errorType: AuthErrorType = AuthErrorType.UNKNOWN;
  errorDetails: AuthErrorDetails | null = null;

  // Network status
  isOnline: boolean = true;
  isCheckingConnection: boolean = false;

  constructor(
    private readonly authService: AuthService,
    private readonly sessionUser: SessionUserService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.setupNetworkListener();
    this.setupConnectionCheckListener();
    this.monitorConfigErrors();
    void this.runCallbackFlow();
  }

  ngOnDestroy(): void {
    clearTimeout(this.loginRedirectTimeout);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async runCallbackFlow(): Promise<void> {
    await this.authService.whenReady();

    if (!this.authService.isAuthenticated()) {
      if (!this.hasError) {
        this.setError(AuthErrorType.TOKEN, "TOKEN_ERROR", "TOKEN_ERROR_MESSAGE");
        this.scheduleLoginRedirect(this.SCHEDULED_REDIRECT_TIMEOUT);
      }
      return;
    }

    if (this.authService.tabUserMismatch()) {
      // Silent SSO returned another user's tokens — this tab may only change
      // its user through an interactive login.
      this.authService.restartLoginAsDifferentUser();
      return;
    }

    this.loadingMessage = "LOGIN_CHECK";

    let authUser: { info: IOpenIDClaims };
    try {
      authUser = await this.authService.getOAuthUserDetails();
      await this.authService.handleAuthenticationSuccess(authUser);
    } catch (error) {
      console.error("[AuthCallback] Callback processing failed:", error);
      this.handleCallbackError(error as Error);
      return;
    }

    if (this.authService.userEnabled && this.sessionUser.getUser()) {
      this.authService.rememberTabUser();
      await this.router.navigateByUrl(consumePostLoginUrl(), { replaceUrl: true });
      return;
    }

    if (this.authService.externProviderAuthenticated && !this.authService.userEnabled) {
      this.setError(AuthErrorType.USER, "ACCOUNT_DISABLED", "ACCOUNT_DISABLED_MESSAGE");
      await this.authService.endDisabledSession();
      return;
    }

    // BFF login failed or returned no user — never navigate unhydrated
    // (the guard would funnel straight back here).
    this.setError(AuthErrorType.UNKNOWN, "USER_DATA_ERROR", "USER_DATA_ERROR_MESSAGE");
  }

  /**
   * Monitor OAuth configuration errors with provider unavailability detection
   */
  private monitorConfigErrors(): void {
    this.authService
      .oauthConfigError$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasConfigError) => {
        if (hasConfigError && !this.hasError) {
          // Check if provider is unavailable
          if (!this.authService.isProviderAvailable()) {
            this.setError(
              AuthErrorType.PROVIDER,
              "PROVIDER_UNAVAILABLE",
              "PROVIDER_UNAVAILABLE_MESSAGE",
            );

            // Auto-redirect to login after showing error
            this.scheduleLoginRedirect(this.SCHEDULED_REDIRECT_TIMEOUT); // 5 second delay
          } else {
            this.setError(
              AuthErrorType.CONFIG,
              "OAUTH_CONFIG_ERROR",
              "OAUTH_CONFIG_ERROR_MESSAGE",
            );

            // Auto-redirect to login after showing error
            this.scheduleLoginRedirect(this.SCHEDULED_REDIRECT_TIMEOUT);
          }
        }
      });
  }

  private scheduleLoginRedirect(delayMs: number): void {
    console.log(`[AuthCallback] Redirecting to login in ${delayMs / 1000}s...`);

    this.loginRedirectTimeout = setTimeout(() => {
      console.log("[AuthCallback] Redirecting to login page");
      this.navigateToLogin();
    }, delayMs);
  }

  /**
   * Monitor connection checking state
   */
  private setupConnectionCheckListener(): void {
    this.authService
      .isCheckingConnection$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isChecking) => {
        this.isCheckingConnection = isChecking;

        if (isChecking) {
          this.loadingMessage = "CHECKING_CONNECTION";
        } else if (this.authService.isAuthenticated()) {
          this.loadingMessage = "LOGIN_CHECK";
        }
      });
  }

  /**
   * Handle callback error based on error type
   */
  private handleCallbackError(
    error: Error | { status?: number; message?: string },
  ): void {
    console.error("[AuthCallback] Callback error:", error);

    // Check provider availability first
    if (!this.authService.isProviderAvailable()) {
      this.setError(
        AuthErrorType.PROVIDER,
        "PROVIDER_UNAVAILABLE",
        "PROVIDER_UNAVAILABLE_MESSAGE",
      );
      return;
    }

    // Categorize by error characteristics
    const status =
      typeof error === "object" && error !== null && "status" in error
        ? error.status
        : undefined;
    if (error?.message?.includes("timeout")) {
      this.setError(
        AuthErrorType.TIMEOUT,
        "LOGIN_TIMEOUT",
        "LOGIN_TIMEOUT_MESSAGE",
      );
    } else if (status === 0) {
      // Network error (provider check passed, so likely network issue)
      this.setError(
        AuthErrorType.NETWORK,
        "NETWORK_ERROR",
        "NETWORK_ERROR_MESSAGE",
      );
    } else if (status === 401 || status === 403) {
      this.setError(AuthErrorType.TOKEN, "TOKEN_ERROR", "TOKEN_ERROR_MESSAGE");
    } else {
      this.setError(
        AuthErrorType.UNKNOWN,
        "LOGIN_PROCESSING_ERROR",
        "UNEXPECTED_ERROR_MESSAGE",
      );
    }
  }

  /**
   * Consolidated error setter - single source of truth
   */
  private setError(
    type: AuthErrorType,
    titleKey: string,
    messageKey: string,
  ): void {
    this.hasError = true;
    this.errorType = type;
    this.errorDetails = {
      title: titleKey,
      message: messageKey,
    };
  }

  /**
   * Setup network status listener
   */
  private setupNetworkListener(): void {
    this.authService
      .isOnline$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOnline: boolean) => {
        this.isOnline = isOnline;

        // If network goes down and not showing error yet
        if (!isOnline && !this.hasError) {
          this.setError(
            AuthErrorType.NETWORK,
            "NETWORK_ERROR",
            "NETWORK_ERROR_MESSAGE",
          );
        }
      });
  }

  /**
   * Navigate back to login page
   */
  navigateToLogin(): void {
    this.router.navigate([""]);
  }
}
